import { InjectionToken } from '@angular/core';

export type SessionStatus =
  /** No session armed (logged out, or no token at bootstrap). */
  | 'inactive'
  /** Timer armed, token valid. */
  | 'active'
  /** A refresh is in flight. */
  | 'refreshing'
  /** Session ended for good; forceLogout has already run. */
  | 'terminated';

export type SessionEndReason =
  /** No activity within the idle window — timedLogout fired. */
  | 'idle'
  /** The refresh token was rejected, or the refresh call failed. */
  | 'refresh-failed'
  /** The access token vanished or became unreadable. */
  | 'no-token'
  /** The user logged out deliberately. */
  | 'manual'
  /** Another tab cleared the shared storage. */
  | 'remote';

export interface SessionConfig {
  /** Fire the refresh this long before the token's `exp`. */
  refreshSkewMs: number;
  /** Never schedule a shorter delay than this (guards against short-token busy loops). */
  minDelayMs: number;
  /** Refresh only if there was user activity within this window. */
  idleWindowMs: number;
  /** DOM events that count as user activity. */
  activityEvents: readonly string[];
  /** Ignore repeat activity events inside this window (cheap throttle). */
  activityThrottleMs: number;
  /** Where to send the user after a forced logout. */
  loginRoute: string;
  /** Circuit breaker: bail out past this many refreshes inside the budget window. */
  maxRefreshesPerWindow: number;
  refreshBudgetWindowMs: number;
}

export const DEFAULT_SESSION_CONFIG: SessionConfig = {
  refreshSkewMs: 60_000,
  minDelayMs: 5_000,
  idleWindowMs: 15 * 60_000,
  // `mousemove` is deliberately absent: a bumped desk should not extend a
  // banking session, and it is by far the noisiest listener on the page.
  activityEvents: [
    'click',
    'keydown',
    'scroll',
    'wheel',
    'touchstart',
    'pointerdown',
    'focus',
  ],
  activityThrottleMs: 1_000,
  loginRoute: '/auth/login',
  maxRefreshesPerWindow: 4,
  refreshBudgetWindowMs: 60_000,
};

export const SESSION_CONFIG = new InjectionToken<SessionConfig>(
  'SESSION_CONFIG',
  {
    providedIn: 'root',
    factory: () => DEFAULT_SESSION_CONFIG,
  },
);
